import { setBenchmarkedGames } from '../features/analysis/helpers/analysisOverviewHelpers'
import {
  ActiveFeatures,
  ActiveFeaturesRequest,
  AnalysisCategory,
  AnswerItem,
  Benchmark,
  BenchmarkedGamesMap,
  InternalAnalysis,
} from '../features/analysis/types/InternalAnalysis'
import { InternalCategoryFeature } from '../types/InternalCategory'
import { customError, optionalJson } from './helpers'
import { categoryFeaturesTag, internalApiserverApi } from './internal-apiserver'

// Unset settings and a game without a completed analysis answer with an empty body.
const benchmarkedGamesUrl = 'users/settings/analysis_previously_benchmarked_games'
const analysisRequest = (id: string) => `analyses/${id}`
const readBenchmarkedGames = (data: unknown): BenchmarkedGamesMap =>
  data && typeof data === 'object' && !Array.isArray(data) ? (data as BenchmarkedGamesMap) : {}

export type CalculatePowerScoreResult = { before: InternalAnalysis; stored: InternalAnalysis }

const analysisApi = internalApiserverApi.enhanceEndpoints({ addTagTypes: ['internalAnalysis', 'benchmarkedGames', 'gameAnalyses'] }).injectEndpoints({
  endpoints: (builder) => ({
    // What legacy's overview loads on every visit; its `progress` is recounted, unlike `GET analyses/{id}`.
    getRefreshedAnalysis: builder.query<InternalAnalysis, string>({
      query: (id) => `analyses/${id}/refresh`,
      providesTags: (_result, _error, id) => [{ type: 'internalAnalysis', id }],
    }),
    getActiveFeatures: builder.query<ActiveFeatures, ActiveFeaturesRequest>({
      query: ({ appId, ...params }) => ({ url: `i/features/forweightset/${appId}`, params }),
      providesTags: ['internalFeatures'],
    }),
    // `null` when the game has no completed analysis in the market.
    getLatestCompletedAnalysis: builder.query<InternalAnalysis | null, Benchmark>({
      query: ({ gameId, marketIso }) => ({
        url: `games/${gameId}/analyses/latest`,
        params: { marketIso, onlyCompleted: true },
        responseHandler: optionalJson,
      }),
    }),
    // Feature legacy id → number of screenshots.
    getScreenshotCounts: builder.query<Record<string, number>, string>({
      query: (gameId) => `screenshots/count/${gameId}`,
    }),
    getBenchmarkedGames: builder.query<BenchmarkedGamesMap, void>({
      query: () => ({ url: benchmarkedGamesUrl, responseHandler: optionalJson }),
      transformResponse: readBenchmarkedGames,
      providesTags: ['benchmarkedGames'],
    }),
    // The setting holds every game's benchmarks and `PUT` replaces it, so it's read just before the write and only one
    // game is changed. Returns the map as stored afterwards.
    saveBenchmarkedGames: builder.mutation<BenchmarkedGamesMap, { gameId: string; benchmarks: Benchmark[] }>({
      async queryFn({ gameId, benchmarks }, _api, _extraOptions, baseQuery) {
        const current = await baseQuery({ url: benchmarkedGamesUrl, responseHandler: optionalJson })
        if (current.error) return { error: current.error }
        const body = setBenchmarkedGames(readBenchmarkedGames(current.data), gameId, benchmarks)
        const saved = await baseQuery({ url: benchmarkedGamesUrl, method: 'PUT', body, responseHandler: 'text' })
        if (saved.error) return { error: saved.error }
        const stored = await baseQuery({ url: benchmarkedGamesUrl, responseHandler: optionalJson })
        if (stored.error) return { error: stored.error }
        return { data: readBenchmarkedGames(stored.data) }
      },
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(analysisApi.util.upsertQueryData('getBenchmarkedGames', undefined, data))
        } catch {}
      },
    }),
    // Legacy's body. Returns the analysis read again, so the caller can check the version.
    updateAnalysisVersion: builder.mutation<InternalAnalysis, { id: string; gameVersion: string }>({
      async queryFn({ id, gameVersion }, _api, _extraOptions, baseQuery) {
        const saved = await baseQuery({ url: `i/analyses/${id}`, method: 'PUT', body: { id, gameVersion }, responseHandler: 'text' })
        if (saved.error) return { error: saved.error }
        const stored = await baseQuery(analysisRequest(id))
        if (stored.error) return { error: stored.error }
        return { data: stored.data as InternalAnalysis }
      },
      invalidatesTags: (_result, _error, { id }) => [{ type: 'internalAnalysis', id }, 'gameAnalyses'],
    }),
    getAnalysisCategory: builder.query<AnalysisCategory, string>({
      query: (categoryId) => `categories/${categoryId}`,
      providesTags: ['internalCategories'],
    }),
    // The category's features in the market, inactive choices included.
    getAnalysisCategoryFeatures: builder.query<InternalCategoryFeature[], { categoryId: string; marketIso: string }>({
      query: ({ categoryId, marketIso }) => `i/categories/${categoryId}/features/${encodeURIComponent(marketIso)}`,
      providesTags: (_result, _error, { categoryId }) => [categoryFeaturesTag(categoryId)],
    }),
    // Legacy's body: every answer of the category, a cleared answer left out. Returns the analysis read again, so the
    // caller can compare the answers.
    saveAnalysisAnswers: builder.mutation<InternalAnalysis, { id: string; categoryId: string; answers: AnswerItem[] }>({
      async queryFn(body, _api, _extraOptions, baseQuery) {
        const saved = await baseQuery({ url: `i/analyses/${body.id}`, method: 'PUT', body, responseHandler: 'text' })
        if (saved.error) return { error: saved.error }
        const stored = await baseQuery(analysisRequest(body.id))
        if (stored.error) return { error: stored.error }
        if (!stored.data) return { error: customError('The analysis could not be read again') }
        return { data: stored.data as InternalAnalysis }
      },
      invalidatesTags: (_result, _error, { id }) => [{ type: 'internalAnalysis', id }, 'gameAnalyses'],
    }),
    // Writes the POWERSCORE, the analysis history and the news cards. The analysis is read before and after, so the
    // caller can check it was finalized. The page refetches when the result dialog closes, as legacy reloads.
    calculatePowerScore: builder.mutation<CalculatePowerScoreResult, string>({
      async queryFn(id, _api, _extraOptions, baseQuery) {
        const before = await baseQuery(analysisRequest(id))
        if (before.error) return { error: before.error }
        const calculated = await baseQuery({ url: `i/analyses/${id}/finalize/lambda`, method: 'PUT', body: { id }, responseHandler: optionalJson })
        if (calculated.error) return { error: calculated.error }
        const stored = await baseQuery(analysisRequest(id))
        if (stored.error) return { error: stored.error }
        if (!stored.data) return { error: customError('The analysis could not be read again') }
        return { data: { before: before.data as InternalAnalysis, stored: stored.data as InternalAnalysis } }
      },
    }),
  }),
})

export const {
  useGetRefreshedAnalysisQuery,
  useGetActiveFeaturesQuery,
  useGetLatestCompletedAnalysisQuery,
  useLazyGetLatestCompletedAnalysisQuery,
  useGetScreenshotCountsQuery,
  useGetBenchmarkedGamesQuery,
  useSaveBenchmarkedGamesMutation,
  useUpdateAnalysisVersionMutation,
  useCalculatePowerScoreMutation,
  useGetAnalysisCategoryQuery,
  useGetAnalysisCategoryFeaturesQuery,
  useSaveAnalysisAnswersMutation,
} = analysisApi

export const invalidateAnalysis = (id: string) => analysisApi.util.invalidateTags([{ type: 'internalAnalysis', id }, 'gameAnalyses'])
